/**
 * PropertyViewingModal - Cal.com Style Property Viewing Scheduler
 * 
 * A beautiful, modern viewing scheduler modal inspired by the AgentMeetingModal.
 * Allows users to select a date and time slot to schedule a property viewing.
 * The booking request is sent to the sales team.
 * 
 * Features:
 * - Interactive calendar for date selection
 * - Time slot grid with visual feedback
 * - Form for user contact details
 * - Sends viewing request to sales email
 */

'use client';

import { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { analyticsEvents, setEnhancedConversionData } from '@/components/analytics/GoogleAnalytics';
import { collectAttribution } from '@/lib/attribution';
import { 
  X, 
  Calendar, 
  User, 
  Mail, 
  Phone, 
  MessageSquare,
  ChevronLeft, 
  ChevronRight, 
  Check, 
  Loader2,
  AlertCircle,
  Home
} from 'lucide-react';

export interface PropertyViewingModalProps {
  propertyId: string;
  propertyName: string;
  /** Property price — passed as conversion value to analytics */
  propertyPrice?: number | undefined;
  organisationId?: string | undefined;
  onClose: () => void;
  onBookingCreated?: (booking: BookingResult) => void;
  messages?: {
    title?: string;
    subtitle?: string;
    selectDate?: string;
    selectTime?: string;
    yourDetails?: string;
    fullName?: string;
    emailAddress?: string;
    phoneNumber?: string;
    message?: string;
    messagePlaceholder?: string;
    confirm?: string;
    confirming?: string;
    success?: string;
    successMessage?: string;
    successEmailNote?: string;
    successNextSteps?: string;
    successStep1?: string;
    successStep2?: string;
    successStep3?: string;
    error?: string;
    errorMessage?: string;
    close?: string;
    required?: string;
    invalidEmail?: string;
    viewingDuration?: string;
    morning?: string;
    afternoon?: string;
    evening?: string;
  };
}

interface BookingFormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

interface BookingResult {
  id: string;
  status: string;
  startTime: string;
}

type ModalStep = 'date' | 'time' | 'details' | 'confirming' | 'success' | 'error';

const defaultMessages = {
  title: 'Schedule a Viewing',
  subtitle: 'Choose a convenient time to visit',
  selectDate: 'Select a Date',
  selectTime: 'Select a Time',
  yourDetails: 'Your Details',
  fullName: 'Full Name',
  emailAddress: 'Email Address',
  phoneNumber: 'Phone Number (Optional)',
  message: 'Additional Notes',
  messagePlaceholder: 'Any special requests or questions?',
  confirm: 'Request Viewing',
  confirming: 'Sending request...',
  success: 'Request Sent!',
  successMessage: 'Your viewing request for {propertyName} has been sent to our team. They will review your preferred time and get back to you shortly to confirm.',
  successEmailNote: 'You\'ll receive an email confirmation once the viewing is confirmed.',
  successNextSteps: 'What happens next?',
  successStep1: 'Our team receives your viewing request',
  successStep2: 'We\'ll check property availability',
  successStep3: 'You\'ll receive a confirmation email',
  error: 'Something went wrong',
  errorMessage: 'Please try again or contact us directly.',
  close: 'Done',
  required: 'This field is required',
  invalidEmail: 'Please enter a valid email',
  viewingDuration: '30 min viewing',
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
};

// Generate days of the month
const generateCalendarDays = (year: number, month: number) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();
  
  const days: (number | null)[] = [];
  
  // Add empty slots for days before the first day
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(null);
  }
  
  // Add the days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }
  
  return days;
};

// Generate time slots
const generateTimeSlots = () => {
  const slots: { time: string; period: 'morning' | 'afternoon' | 'evening' }[] = [];
  
  // Morning slots (9 AM - 12 PM)
  for (let hour = 9; hour < 12; hour++) {
    slots.push({ time: `${hour.toString().padStart(2, '0')}:00`, period: 'morning' });
    slots.push({ time: `${hour.toString().padStart(2, '0')}:30`, period: 'morning' });
  }
  
  // Afternoon slots (12 PM - 5 PM)
  for (let hour = 12; hour < 17; hour++) {
    slots.push({ time: `${hour.toString().padStart(2, '0')}:00`, period: 'afternoon' });
    slots.push({ time: `${hour.toString().padStart(2, '0')}:30`, period: 'afternoon' });
  }
  
  // Evening slots (5 PM - 7 PM)
  for (let hour = 17; hour < 19; hour++) {
    slots.push({ time: `${hour.toString().padStart(2, '0')}:00`, period: 'evening' });
    slots.push({ time: `${hour.toString().padStart(2, '0')}:30`, period: 'evening' });
  }
  
  return slots;
};

const TIME_SLOTS = generateTimeSlots();
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export function PropertyViewingModal({
  propertyId,
  propertyName,
  propertyPrice,
  organisationId,
  onClose,
  onBookingCreated,
  messages: customMessages = {},
}: PropertyViewingModalProps) {
  const messages = { ...defaultMessages, ...customMessages };
  
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [step, setStep] = useState<ModalStep>('date');
  const [formData, setFormData] = useState<BookingFormData>({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof BookingFormData, string>>>({});
  const [bookingResult, setBookingResult] = useState<BookingResult | null>(null);

  const calendarDays = useMemo(
    () => generateCalendarDays(currentYear, currentMonth),
    [currentYear, currentMonth]
  );

  const isDateDisabled = useCallback((day: number) => {
    const date = new Date(currentYear, currentMonth, day);
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    
    // Disable past dates and Sundays
    return date < todayStart || date.getDay() === 0;
  }, [currentYear, currentMonth]);

  const handlePrevMonth = useCallback(() => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(prev => prev - 1);
    } else {
      setCurrentMonth(prev => prev - 1);
    }
  }, [currentMonth]);

  const handleNextMonth = useCallback(() => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(prev => prev + 1);
    } else {
      setCurrentMonth(prev => prev + 1);
    }
  }, [currentMonth]);

  const handleDateSelect = useCallback((day: number) => {
    if (!isDateDisabled(day)) {
      setSelectedDate(new Date(currentYear, currentMonth, day));
      setStep('time');
    }
  }, [currentYear, currentMonth, isDateDisabled]);

  const handleTimeSelect = useCallback((time: string) => {
    setSelectedTime(time);
    setStep('details');
  }, []);

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof BookingFormData, string>> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = messages.required;
    }
    
    if (!formData.email.trim()) {
      newErrors.email = messages.required;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = messages.invalidEmail;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, messages]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm() || !selectedDate || !selectedTime) {
      return;
    }
    
    setStep('confirming');
    
    try {
      // Construct the datetime
      const timeParts = selectedTime.split(':');
      const hours = parseInt(timeParts[0] || '0', 10);
      const minutes = parseInt(timeParts[1] || '0', 10);
      const startTime = new Date(selectedDate);
      startTime.setHours(hours, minutes, 0, 0);
      const endTime = new Date(startTime.getTime() + 30 * 60 * 1000); // 30 min viewing
      
      const attribution = collectAttribution();
      const response = await fetch('/api/property-viewings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organisationId,
          propertyId,
          propertyName,
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          subject: `Property Viewing Request: ${propertyName}`,
          description: formData.message,
          contact: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
          },
          metadata: attribution,
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to create booking');
      }
      
      const result = await response.json();
      setBookingResult(result);
      setStep('success');
      onBookingCreated?.(result);

      const [firstName, ...lastParts] = formData.name.trim().split(/\s+/);
      setEnhancedConversionData({
        email: formData.email,
        phone: formData.phone,
        firstName,
        lastName: lastParts.join(' ') || undefined,
      });

      analyticsEvents.formSubmit('property_viewing', window.location.pathname);
      analyticsEvents.generateLead('property_viewing', propertyPrice, {
        propertyId,
        propertyName,
        email: formData.email,
        phone: formData.phone,
      });

      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'form_submission',
          form_name: 'property_viewing',
          form_location: window.location.pathname,
        });
      }
    } catch (error) {
      console.error('Booking error:', error);
      setStep('error');
    }
  }, [validateForm, selectedDate, selectedTime, organisationId, propertyId, propertyName, formData, onBookingCreated]);

  const handleChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof BookingFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  }, [errors]);

  const formatSelectedDateTime = () => {
    if (!selectedDate || !selectedTime) return '';
    
    const dateStr = selectedDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
    
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const timeStr = new Date(0, 0, 0, hours, minutes).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    
    return `${dateStr} at ${timeStr}`;
  };

  // Check if we can go to previous month (not before current month)
  const canGoPrevMonth = currentYear > today.getFullYear() || 
    (currentYear === today.getFullYear() && currentMonth > today.getMonth());

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* Header - Navy/Gold Allys Theme */}
        <div className="bg-gradient-to-br from-[#243462] to-[#1a2647] p-6 pb-16">
          <div className="flex items-center gap-4">
            <div className="relative w-14 h-14 rounded-full overflow-hidden ring-2 ring-[#BBA339]/30 shadow-xl bg-gradient-to-br from-[#BBA339] to-[#E4CE4A] flex items-center justify-center">
              <Home className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white font-inter">
                {messages.title}
              </h3>
              <p className="text-sm text-white/80 truncate max-w-[250px] font-inter">
                {propertyName} • {messages.viewingDuration}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative -mt-8 mx-4 mb-4 bg-white rounded-2xl shadow-lg border border-gray-100">
          <AnimatePresence mode="wait">
            {/* Date Selection Step */}
            {step === 'date' && (
              <motion.div
                key="date"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-5"
              >
                <p className="text-sm font-medium text-gray-600 mb-4 font-inter">
                  {messages.selectDate}
                </p>

                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={handlePrevMonth}
                    disabled={!canGoPrevMonth}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-600" />
                  </button>
                  <h4 className="text-base font-semibold text-gray-900 font-inter">
                    {MONTHS[currentMonth]} {currentYear}
                  </h4>
                  <button
                    onClick={handleNextMonth}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-600" />
                  </button>
                </div>

                {/* Weekday Headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {WEEKDAYS.map(day => (
                    <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((day, index) => {
                    if (day === null) {
                      return <div key={`empty-${index}`} className="aspect-square" />;
                    }
                    
                    const disabled = isDateDisabled(day);
                    const isSelected = selectedDate?.getDate() === day &&
                      selectedDate?.getMonth() === currentMonth &&
                      selectedDate?.getFullYear() === currentYear;
                    const isToday = day === today.getDate() &&
                      currentMonth === today.getMonth() &&
                      currentYear === today.getFullYear();
                    
                    return (
                      <button
                        key={day}
                        onClick={() => handleDateSelect(day)}
                        disabled={disabled}
                        className={`
                          aspect-square rounded-xl text-sm font-medium transition-all duration-200 font-inter
                          ${disabled 
                            ? 'text-gray-300 cursor-not-allowed' 
                            : 'hover:bg-[#243462]/10 cursor-pointer'}
                          ${isSelected 
                            ? 'bg-[#243462] text-white shadow-lg shadow-[#243462]/30' 
                            : isToday 
                              ? 'bg-[#BBA339]/20 text-[#243462]'
                              : 'text-gray-700'}
                        `}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Time Selection Step */}
            {step === 'time' && (
              <motion.div
                key="time"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-5"
              >
                <button
                  onClick={() => setStep('date')}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </button>

                <p className="text-sm font-medium text-gray-600 mb-4 font-inter">
                  {messages.selectTime}
                </p>

                <div className="max-h-[280px] overflow-y-auto pr-2 space-y-4">
                  {(['morning', 'afternoon', 'evening'] as const).map(period => {
                    const periodSlots = TIME_SLOTS.filter(s => s.period === period);
                    if (periodSlots.length === 0) return null;
                    
                    return (
                      <div key={period}>
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                          {messages[period]}
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                          {periodSlots.map(({ time }) => {
                            const [hours, minutes] = time.split(':').map(Number);
                            const displayTime = new Date(0, 0, 0, hours, minutes).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true,
                            });
                            
                            const isSelected = selectedTime === time;
                            
                            return (
                              <button
                                key={time}
                                onClick={() => handleTimeSelect(time)}
                                className={`
                                  px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 font-inter
                                  ${isSelected 
                                    ? 'bg-[#243462] text-white shadow-lg shadow-[#243462]/30' 
                                    : 'bg-gray-100 text-gray-700 hover:bg-[#243462]/10 hover:text-[#243462]'}
                                `}
                              >
                                {displayTime}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Details Step */}
            {step === 'details' && (
              <motion.div
                key="details"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-5"
              >
                <button
                  onClick={() => setStep('time')}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Change time
                </button>

                {/* Selected DateTime Summary */}
                <div className="flex items-center gap-3 p-3 bg-[#243462]/5 rounded-xl mb-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#243462]/10 flex items-center justify-center">
                    <Home className="w-5 h-5 text-[#243462]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 font-inter">
                      {formatSelectedDateTime()}
                    </p>
                    <p className="text-xs text-gray-500">
                      {messages.viewingDuration}
                    </p>
                  </div>
                </div>

                <p className="text-sm font-medium text-gray-600 mb-4 font-inter">
                  {messages.yourDetails}
                </p>

                <form className="space-y-3">
                  {/* Name */}
                  <div>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={messages.fullName}
                        className={`w-full pl-10 pr-4 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#243462]/20 focus:border-[#243462] outline-none transition-all font-inter ${
                          errors.name ? 'border-red-500' : 'border-gray-200'
                        }`}
                      />
                    </div>
                    {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={messages.emailAddress}
                        className={`w-full pl-10 pr-4 py-2.5 text-sm border rounded-xl focus:ring-2 focus:ring-[#243462]/20 focus:border-[#243462] outline-none transition-all font-inter ${
                          errors.email ? 'border-red-500' : 'border-gray-200'
                        }`}
                      />
                    </div>
                    {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                  </div>

                  {/* Phone */}
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={messages.phoneNumber}
                      className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#243462]/20 focus:border-[#243462] outline-none transition-all font-inter"
                    />
                  </div>

                  {/* Message */}
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={messages.messagePlaceholder}
                      rows={2}
                      className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#243462]/20 focus:border-[#243462] outline-none transition-all resize-none font-inter"
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#243462] to-[#1a2647] text-white rounded-xl font-semibold hover:shadow-lg transition-all font-inter"
                  >
                    <Calendar className="w-5 h-5" />
                    {messages.confirm}
                  </button>
                </form>
              </motion.div>
            )}

            {/* Confirming Step */}
            {step === 'confirming' && (
              <motion.div
                key="confirming"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="p-8 flex flex-col items-center justify-center min-h-[300px]"
              >
                <div className="w-16 h-16 rounded-full bg-[#243462]/10 flex items-center justify-center mb-4">
                  <Loader2 className="w-8 h-8 text-[#243462] animate-spin" />
                </div>
                <p className="text-lg font-semibold text-gray-900 font-inter">
                  {messages.confirming}
                </p>
              </motion.div>
            )}

            {/* Success Step */}
            {step === 'success' && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="p-6"
              >
                {/* Success Header with Animation */}
                <div className="text-center mb-6">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', damping: 15, stiffness: 300, delay: 0.1 }}
                    className="w-16 h-16 rounded-full bg-gradient-to-br from-[#BBA339]/20 to-[#E4CE4A]/20 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#BBA339]/10"
                  >
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', damping: 12, stiffness: 200, delay: 0.2 }}
                    >
                      <Check className="w-8 h-8 text-[#BBA339]" strokeWidth={3} />
                    </motion.div>
                  </motion.div>
                  
                  <motion.h4 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-xl font-bold text-gray-900 mb-2 font-inter"
                  >
                    {messages.success}
                  </motion.h4>
                  
                  <motion.p 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-sm text-gray-600 font-inter"
                  >
                    {messages.successMessage.replace('{propertyName}', propertyName)}
                  </motion.p>
                </div>

                {/* Viewing Summary Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-gray-50 rounded-2xl p-4 mb-5"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#BBA339] to-[#E4CE4A] flex items-center justify-center shadow-md flex-shrink-0">
                      <Home className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate font-inter">
                        {propertyName}
                      </p>
                      <p className="text-sm text-gray-600 font-inter">
                        {formatSelectedDateTime()}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                          Awaiting Confirmation
                        </span>
                      </div>
                    </div>
                  </div>
                  {bookingResult && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500 font-inter">
                        Reference: <span className="font-mono text-gray-700">{bookingResult.id.slice(0, 12).toUpperCase()}</span>
                      </p>
                    </div>
                  )}
                </motion.div>

                {/* What Happens Next */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mb-6"
                >
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 font-inter">
                    {messages.successNextSteps}
                  </p>
                  <div className="space-y-2">
                    {[
                      { num: 1, text: messages.successStep1, delay: 0.7 },
                      { num: 2, text: messages.successStep2, delay: 0.8 },
                      { num: 3, text: messages.successStep3, delay: 0.9 },
                    ].map((stepItem) => (
                      <motion.div
                        key={stepItem.num}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: stepItem.delay }}
                        className="flex items-center gap-3"
                      >
                        <div className="w-6 h-6 rounded-full bg-[#243462]/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-[#243462]">{stepItem.num}</span>
                        </div>
                        <p className="text-sm text-gray-600 font-inter">
                          {stepItem.text}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                {/* Email Note */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1 }}
                  className="text-xs text-gray-500 text-center mb-4 font-inter"
                >
                  {messages.successEmailNote}
                </motion.p>

                {/* Close Button */}
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.1 }}
                  onClick={onClose}
                  className="w-full px-6 py-3 bg-gradient-to-r from-[#243462] to-[#1a2647] text-white rounded-xl font-semibold hover:shadow-lg transition-all font-inter"
                >
                  {messages.close}
                </motion.button>
              </motion.div>
            )}

            {/* Error Step */}
            {step === 'error' && (
              <motion.div
                key="error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="p-8 flex flex-col items-center justify-center min-h-[300px]"
              >
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2 font-inter">
                  {messages.error}
                </h4>
                <p className="text-sm text-gray-600 text-center mb-6 font-inter">
                  {messages.errorMessage}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setStep('details')}
                    className="px-6 py-2.5 bg-[#243462] text-white rounded-xl font-medium hover:bg-[#1a2647] transition-colors font-inter"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={onClose}
                    className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors font-inter"
                  >
                    {messages.close}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default PropertyViewingModal;
